import type { CommunityMember } from "@circle-react/types";

import type { ConnectionResponse } from "@/react/api/admin/connections";
import { ConnectionStatus } from "@/react/api/admin/connections";
import { useCurrentUser } from "@/react/contexts/punditUserContext";

export interface ConnectionButtonState {
  connection?: ConnectionResponse;
  isActive: boolean;
  isCurrentUser: boolean;
  isEmpty: boolean;
  isFlaggedForModeration: boolean;
  isPendingReceived: boolean;
  isPendingSent: boolean;
}

export const useConnectionButtonState = (
  member: CommunityMember | undefined,
): ConnectionButtonState => {
  const currentUser = useCurrentUser();

  if (!member) {
    return {
      isActive: false,
      isCurrentUser: false,
      isEmpty: true,
      isFlaggedForModeration: false,
      isPendingReceived: false,
      isPendingSent: false,
    };
  }

  const connection = member.connection_to_current_community_member;

  const isCurrentUser = currentUser?.id === member.user_id;

  const isDetachedOrWithdrawn =
    connection &&
    (connection.status === ConnectionStatus.DETACHED ||
      connection.status === ConnectionStatus.WITHDRAWN);

  const isIgnored =
    connection && connection.status === ConnectionStatus.IGNORED;

  const isPendingSent =
    connection &&
    connection.status === ConnectionStatus.PENDING &&
    connection.initiator.user_id === currentUser?.id;

  const isFlaggedForModeration =
    connection &&
    connection.status === ConnectionStatus.PENDING &&
    connection.initiator.user_id !== currentUser?.id &&
    Boolean(connection.is_flagged_for_moderation);

  const isPendingReceived =
    connection &&
    connection.status === ConnectionStatus.PENDING &&
    connection.initiator.user_id !== currentUser?.id &&
    !connection.is_flagged_for_moderation;

  const isEmpty = !connection || isDetachedOrWithdrawn || isIgnored;
  const isActive = connection?.status === ConnectionStatus.ACTIVE;

  return {
    isEmpty: Boolean(isEmpty),
    isActive: Boolean(isActive),
    isFlaggedForModeration: Boolean(isFlaggedForModeration),
    isPendingSent: Boolean(isPendingSent),
    isPendingReceived: Boolean(isPendingReceived),
    isCurrentUser,
    connection,
  };
};
